import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { Redirect, Route } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import Navbar from 'components/Navbar';
import Notification from 'components/Notification';
import ProfileDropdown from 'components/ProfileDropdown';
import Loader from 'components/Loader';

import PATHS from 'routes/paths';
import { getOrderList } from 'redux/slices/order';
import { getFundraiserList } from 'redux/slices/fundraiser';
import { getProductList } from 'redux/slices/product';
import { getStoreList, storeListSelector } from 'redux/slices/javelinStore';
import { getCompanyOnInit } from 'redux/slices/company';
import { getMe, logout } from 'redux/slices/auth';
import { collapseSidebar } from 'redux/slices/sidebar';
import { usePrevious } from 'hooks';
import CoreRoute from './CoreRoute';
import { BrandContext } from 'utils/brand-context';

function PrivateRoute({ component: Component, title, ...rest }) {
  const dispatch = useDispatch();
  const [isReady, setIsReady] = useState(false);
  const brand = useContext(BrandContext);
  const id = rest.computedMatch?.params?.id || null;
  const authenticated = useSelector((state) => state.auth.authenticated);
  const isCollapsed = useSelector((state) => state.sidebar.isCollapsed);
  const storeId = useSelector((state) => state.javelinStore.current);
  const hopRedirecting = useSelector((state) => state.company.hopRedirecting);
  const previousStoreId = usePrevious(storeId);
  const storeCount = useSelector(storeListSelector.selectTotal);
  const previousPath = usePrevious(rest.path);

  const pageIsVisible = isReady && authenticated;
  const isOnboarding = (!storeCount && brand.hasOnboarding) || hopRedirecting;

  useEffect(() => {
    if (previousPath !== rest.path) {
      window.scrollTo(0, 0);
    }
  }, [previousPath, rest.path]);

  useEffect(() => {
    const fetchAll = async () => {
      const getMeAction = await dispatch(getMe());
      if (getMeAction.type.endsWith('fulfilled')) {
        await dispatch(getCompanyOnInit());
      } else if (
        getMeAction.type.endsWith('rejected') &&
        getMeAction.payload?.status === 423
      ) {
        dispatch(logout());
        return setIsReady(true);
      }
      const _action = await dispatch(getStoreList());
      if (_action.type.endsWith('fulfilled') && _action.payload.data?.length) {
        if (brand.hasFundraisers) {
          dispatch(getFundraiserList({ shouldFetchLive: true }));
          dispatch(getOrderList());
        }
        if (brand.hasProducts) {
          dispatch(getProductList({}));
        }
      }
      setIsReady(true);
    };
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOnStoreChange = useCallback(() => {
    if (!!storeId && !!previousStoreId && storeId !== previousStoreId) {
      if (brand.hasFundraisers) {
        dispatch(getFundraiserList({ shouldFetchLive: true }));
        dispatch(getOrderList());
      }
      if (brand.hasProducts) {
        dispatch(getProductList({}));
      }
    }
  }, [
    dispatch,
    previousStoreId,
    storeId,
    brand.hasProducts,
    brand.hasFundraisers,
  ]);

  const onCollapse = () => {
    dispatch(collapseSidebar(!isCollapsed));
  };

  useEffect(() => {
    fetchOnStoreChange();
  }, [fetchOnStoreChange]);

  const wrapperClassName = useMemo(() => {
    if (isCollapsed) {
      return 'wrapper-collapsed';
    } else {
      return 'wrapper-uncollapsed';
    }
  }, [isCollapsed]);

  if (!isReady) return <Loader />;

  if (isOnboarding && [PATHS.home, PATHS.profile].indexOf(rest.path) === -1)
    return <Redirect to={PATHS.home} />;

  return (
    <CoreRoute title={id ? `${title}: ${id}` : title}>
      <div className="flex">
        {pageIsVisible && (
          <Navbar
            onCollapse={onCollapse}
            isCollapsed={isCollapsed}
            isOnboarding={isOnboarding}
          />
        )}
        <div
          className={`min-h-screen box-border px-4 lg:px-14 py-24 lg:py-24 bg-javelin-background overflow-x-hidden overflow-y-auto relative ${
            pageIsVisible ? wrapperClassName : '100vw mx-auto'
          }`}
        >
          <Notification />
          <ProfileDropdown />
          <Route
            {...rest}
            render={(routeProps) =>
              pageIsVisible ? <Component {...routeProps} /> : <Loader />
            }
          />
        </div>
      </div>
    </CoreRoute>
  );
}
export default PrivateRoute;
